import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { FormField } from "../../components/FormField";
import { FormSection } from "../../components/FormSection";
import { cn } from "@/lib/utils";

interface FormSidebarProps {
    formData: any;
    onInputChange: (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => void;
    onSubmit: (e: React.FormEvent) => void;
    loading: boolean;
    isWaccCalculated: boolean;
    hasSensibilizaciones: boolean;
    canSensibilizeBeta: boolean;
    dates: string[];
    sectors: string[];
    instruments: string[];
    bonos: string[];
    countries: string[];
    industryTranslations: Record<string, string>;
    bonosTranslations: Record<string, string>;
    countriesTranslations: Record<string, string>;
    countryLocalCurrencies: Record<string, string>;
    chatbotComponent?: React.ReactNode;
    onSearchSectorBeta: () => void;
    isSearchingBeta: boolean;
}

export const FormSidebar: React.FC<FormSidebarProps> = ({
    formData,
    onInputChange,
    onSubmit,
    loading,
    isWaccCalculated,
    canSensibilizeBeta,
    dates,
    sectors,
    bonos,
    countries,
    industryTranslations,
    bonosTranslations,
    countriesTranslations,
    countryLocalCurrencies,
    onSearchSectorBeta,
    isSearchingBeta,
}) => {
    const [collapsed, setCollapsed] = useState({
        step1: false,
        step2: false,
        step3: false,
        step4: false,
    });
    const hasAutoCollapsed = useRef(false);

    const isSection1Complete = Boolean(
        formData.date && formData.sector && formData.instrument && formData.bono
    );
    const isSection2Complete = Boolean(formData.country);
    const isSection2Disabled = !isSection1Complete;
    const isSection3Disabled = !(isSection1Complete && isSection2Complete);

    const [betaInfoVisible, setBetaInfoVisible] = useState(false);
    const betaHideTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleBetaPanelEnter = () => {
        if (betaHideTimeout.current) clearTimeout(betaHideTimeout.current);
    };

    const handleBetaPanelLeave = () => {
        betaHideTimeout.current = setTimeout(() => setBetaInfoVisible(false), 120);
    };

    const toggleCollapse = (step: "step1" | "step2" | "step3" | "step4") => {
        setCollapsed((prev) => ({ ...prev, [step]: !prev[step] }));
    };

    useEffect(() => {
        if (isWaccCalculated && !hasAutoCollapsed.current) {
            setCollapsed({ step1: false, step2: false, step3: false, step4: false });

            hasAutoCollapsed.current = true;
        }
    }, [isWaccCalculated]);

    const handleCustomInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        onInputChange(e);
    };

    const dynamicCurrenciesList = (() => {
        const localCode = formData.country
            ? countryLocalCurrencies[formData.country]
            : null;
        if (!localCode || localCode === "USD") {
            return ["USD"];
        }
        return ["USD", localCode];
    })();

    return (
        <>
            <form id="wacc-form" onSubmit={onSubmit} className="flex h-full flex-col">
                <div className="flex-1 min-h-0 bg-white p-2 pb-0 flex flex-col">
                    <div className="flex-1 overflow-y-auto flex flex-col gap-2">
                        {/* Section 1: Industry */}
                        <div data-tour="kapital-step-1" className="rounded-lg">
                        <FormSection
                            title="Inputs de la industria"
                            step={1}
                            isCollapsed={collapsed.step1}
                            onToggleCollapse={() => toggleCollapse("step1")}
                        >
                            <div className="flex gap-1 flex-col">
                                <FormField
                                    label="Fecha"
                                    name="date"
                                    type="select"
                                    value={formData.date}
                                    onChange={handleCustomInputChange}
                                    options={dates}
                                    layout="horizontal"
                                    inputClassName="col-span-12"
                                    required
                                />
                                <FormField
                                    label="Sector"
                                    name="sector"
                                    type="select"
                                    value={formData.sector}
                                    onChange={handleCustomInputChange}
                                    options={sectors}
                                    translations={industryTranslations}
                                    //disabled={hasSensibilizaciones || isWaccCalculated}
                                    layout="horizontal"
                                    inputClassName="col-span-18"
                                    required
                                />
                                  <div className="relative w-full border border-transparent">
                                    <div data-tour="kapital-beta-sensitivity">
                                    <FormField
                                      label="Beta desapalancado"
                                      name="beta_unlevered_industry"
                                      type="number"
                                      step="any"
                                      value={formData.beta_subsector || formData.beta_unlevered_industry}
                                      onChange={handleCustomInputChange}
                                      suffix="coef."
                                      layout="horizontal"
                                      showClearButton={false}
                                      inputClassName="col-span-9"
                                      disabled={!isWaccCalculated}
                                    />
                                    </div>

                                    {formData.subsector === "Personalizado" && (
                                      <span className="absolute left-0 -top-2.5 text-[9px] font-black uppercase tracking-wider text-valora-primary bg-white px-1 rounded">
                                        Personalizado
                                      </span>
                                    )}

                                    {isWaccCalculated && canSensibilizeBeta && (
                                      <div className="absolute right-0 top-0 bottom-0 w-[25%] flex items-center justify-end">
                                        <button
                                          data-tour="kapital-beta-sensitivity-btn"
                                          type="button"
                                          onClick={() => {
                                            setBetaInfoVisible(false);
                                            onSearchSectorBeta();
                                          }}
                                          disabled={isSearchingBeta || !formData.sector}
                                          className={cn(
                                            "text-[10px] w-full h-10 py-0.5 px-1 rounded-sm text-wrap font-bold cursor-pointer flex items-center justify-center text-center leading-tight tracking-wider",
                                            "text-valora-primary bg-white border border-valora-primary focus:outline-none",
                                            "disabled:cursor-not-allowed disabled:opacity-50"
                                          )}
                                        >
                                          {isSearchingBeta ? (
                                            "Buscando..."
                                          ) : (
                                            <span>
                                              Obtén Tu Beta
                                              <br />
                                              Por Subsector
                                            </span>
                                          )}
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                <FormField
                                    label="Tasa libre de riesgo"
                                    name="instrument"
                                    type="text"
                                    value={formData.instrument}
                                    onChange={handleCustomInputChange}
                                    layout="horizontal"
                                    inputClassName="col-span-18"
                                    required
                                    disabled
                                />
                                <FormField
                                    label="Duración"
                                    name="bono"
                                    type="select"
                                    value={formData.bono}
                                    onChange={handleCustomInputChange}
                                    options={bonos}
                                    translations={bonosTranslations}
                                    layout="horizontal"
                                    inputClassName="col-span-12"
                                    showClearButton={false}
                                    required
                                />
                            </div>
                        </FormSection>
                        </div>

                        {/* Section 2: Sector */}
                        <div data-tour="kapital-step-2" className="rounded-lg">
                        <FormSection
                            title="Inputs del sector"
                            step={2}
                            isCollapsed={collapsed.step2}
                            onToggleCollapse={() => toggleCollapse("step2")}
                            disabled={isSection2Disabled}
                            disabledMessage="Completa los Inputs de la industria primero"
                        >
                            <section className="flex gap-1 flex-col">
                                <FormField
                                    label="País"
                                    name="country"
                                    type="select"
                                    value={formData.country}
                                    onChange={handleCustomInputChange}
                                    options={countries}
                                    translations={countriesTranslations}
                                    layout="horizontal"
                                    inputClassName="col-span-11"
                                    required
                                    disabled={isSection2Disabled}
                                />
                                <FormField
                                    label="Devaluación"
                                    name="devaluation"
                                    type="number"
                                    step="any"
                                    disabled
                                    value={formData.devaluation}
                                    onChange={handleCustomInputChange}
                                    tooltip="Obtenido de Marco Macroeconómico Multianual por país"
                                    suffix="%"
                                    layout="horizontal"
                                    showClearButton={false}
                                    inputClassName="col-span-8"
                                />
                                <FormField
                                    label="Tasa impositiva"
                                    name="tax"
                                    type="number"
                                    disabled
                                    min={0}
                                    max={100}
                                    step="any"
                                    value={formData.tax}
                                    onChange={handleCustomInputChange}
                                    tooltip="IR declarado por cada país. Reporte EY."
                                    suffix="%"
                                    layout="horizontal"
                                    showClearButton={false}
                                    inputClassName="col-span-8"
                                />
                            </section>
                        </FormSection>
                        </div>

                        {/* Section 3: Company */}
                        <div data-tour="kapital-step-3" className="rounded-lg">
                        <FormSection
                            title="Inputs de su empresa"
                            step={3}
                            isCollapsed={collapsed.step3}
                            onToggleCollapse={() => toggleCollapse("step3")}
                            toggle={formData.typeId}
                            onToggle={() =>
                                handleCustomInputChange({
                                    target: { name: "typeId", value: !formData.typeId },
                                } as any)
                            }
                            disabled={isSection3Disabled}
                            disabledMessage="Completa los Inputs de la industria y del sector primero"
                        >
                            <section className="flex gap-1 flex-col">
                                {/* Costo de deuda con selector de moneda */}
                                <FormField
                                    label="Costo de deuda | Empresa"
                                    name="kd"
                                    type="number"
                                    min={0}
                                    max={100}
                                    step="any"
                                    value={formData.kd}
                                    onChange={handleCustomInputChange}
                                    placeholder=""
                                    suffix="%"
                                    layout="horizontal"
                                    showClearButton={false}
                                    maxDecimals={2}
                                    inputClassName="col-span-12"
                                    prefixSelect={{
                                        name: "currency",
                                        value: formData.currency,
                                        options: dynamicCurrenciesList,
                                        className: "w-14",
                                    }}
                                    disabled={isSection3Disabled}
                                />
                                <FormField
                                    label="% de deuda"
                                    name="debt"
                                    type="number"
                                    min={0}
                                    max={100}
                                    step="any"
                                    value={formData.debt}
                                    onChange={handleCustomInputChange}
                                    tooltip="Pasivo Financiero / (Pasivo Financiero/Patrimonio)"
                                    suffix="%"
                                    layout="horizontal"
                                    maxDecimals={2}
                                    inputClassName="col-span-8"
                                    showClearButton={false}
                                    disabled={isSection3Disabled}
                                />
                                <FormField
                                    label="% de capital"
                                    name="capital"
                                    type="number"
                                    min={0}
                                    max={100}
                                    step="any"
                                    value={formData.capital}
                                    onChange={handleCustomInputChange}
                                    tooltip="Patrimonio / (Pasivo Financiero/Patrimonio)"
                                    suffix="%"
                                    layout="horizontal"
                                    inputClassName="col-span-8"
                                    maxDecimals={2}
                                    showClearButton={false}
                                    disabled={isSection3Disabled}
                                />
                            </section>
                        </FormSection>
                        </div>
                        {/* Section 4: Sensibilizaciones (Disabled/Hidden) */}
                        {false && isWaccCalculated && canSensibilizeBeta && (
                            <FormSection
                                title="Sensibiliza tu Beta"
                                step={4}
                                isCollapsed={collapsed.step4}
                                onToggleCollapse={() => toggleCollapse("step4")}
                                toggle={formData.typeId}
                                onToggle={() => toggleCollapse("step1")}
                            >
                                <section className="flex gap-1 flex-col">
                                    <FormField
                                        label="Beta desapalancado"
                                        name="beta_unlevered"
                                        type="number"
                                        step="any"
                                        value={formData.subsector_sensibilizacion ? formData.beta_unlevered || "" : ""}
                                        onChange={handleCustomInputChange}
                                        layout="horizontal"
                                        inputClassName="col-span-6"
                                        showClearButton={true}
                                        maxDecimals={2}
                                    />
                                </section>
                            </FormSection>
                        )}
                        {/* {chatbotComponent} */}
                    </div>

                    {/* Footer - Submit Button */}
                    <div
                        data-tour="kapital-calculate"
                        className={`sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 transition-[box-shadow] duration-300 w-full`}
                    >
                        <button
                            type="submit"
                            form="wacc-form"
                            disabled={loading}
                            className={`
                cursor-pointer w-full py-3 px-6 rounded-lg font-bold text-xs md:text-sm transition-[background-color,box-shadow,transform,color] duration-200 
                ${loading
                                    ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                                    : "bg-valora-primary text-white hover:bg-valora-secondary shadow-lg hover:shadow-xl active:scale-[0.96]"
                                }
              `}
                        >
                            {loading ? (
                                <span className="flex items-center justify-center gap-2">
                                    <svg
                                        className="animate-spin h-5 w-5"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        />
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                        />
                                    </svg>
                                    Calculando...
                                </span>
                            ) : isWaccCalculated ? (
                                "COMPARAR TU WACC"
                            ) : (
                                "CALCULA TU WACC"
                            )}
                        </button>
                    </div>
                </div>
            </form>

            {createPortal(
                <div
                    onMouseEnter={handleBetaPanelEnter}
                    onMouseLeave={handleBetaPanelLeave}
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        zIndex: 99999,
                        transform: betaInfoVisible ? "translateY(-100%) translateY(-0px) scale(1)" : "translateY(-100%) translateY(6px) scale(0.97)",
                        opacity: betaInfoVisible ? 1 : 0,
                        pointerEvents: betaInfoVisible ? "auto" : "none",
                        transition: "opacity 200ms ease, transform 200ms ease",
                        transformOrigin: "bottom right",
                    }}
                    className="max-w-[min(288px,calc(100vw-32px))] w-auto min-w-[200px] bg-white rounded-lg shadow-2xl p-4"
                >
                    <div className="flex items-start gap-3">
                        <div className="mt-0.5 shrink-0 bg-valora-primary/10 p-1.5 rounded-full">
                            <svg className="w-4 h-4 text-valora-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <div>
                            <h4 className="text-[11px] font-bold text-valora-primary mb-1 uppercase tracking-wider">¿Para qué sirve?</h4>
                            <p className="text-[11px] text-gray-600 leading-relaxed">
                                La IA identifica empresas comparables, calcula el beta desapalancado de cada una y obtiene un promedio ponderado por Enterprise Value. Refleja el riesgo operativo del sector eliminando el efecto de la estructura de capital, para estimar el costo de capital en valorizaciones DCF.
                            </p>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

